import { PaletteRecord } from './database';

/**
 * Plugin types supported by Chromis
 */
export type PluginType = 'figma' | 'photoshop' | 'illustrator';

/**
 * Plugin connection status
 */
export type PluginStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

/**
 * Message types for plugin communication
 */
export type PluginMessageType =
  | 'connect'
  | 'disconnect'
  | 'push_palette'
  | 'pull_palette'
  | 'push_tokens'
  | 'pull_tokens'
  | 'status_request'
  | 'status_response'
  | 'error';

/**
 * Plugin message structure
 */
export interface PluginMessage {
  type: PluginMessageType;
  pluginType: PluginType;
  payload?: unknown;
  timestamp: number;
  messageId: string;
}

/**
 * Plugin connection info
 */
export interface PluginConnection {
  type: PluginType;
  status: PluginStatus;
  lastConnected?: number;
  version?: string;
  error?: string;
}

/**
 * Token format for design tools
 */
export interface DesignToken {
  name: string;
  value: string;
  type: 'color';
  description?: string;
  extensions?: {
    [key: string]: unknown;
  };
}

/**
 * Plugin Service - Handles communication with external design tool plugins
 * 
 * This is a stub implementation that provides the infrastructure for plugin
 * communication. Actual plugin implementations are in the /plugins directory.
 */
export class PluginService {
  private connections: Map<PluginType, PluginConnection> = new Map();
  private messageHandlers: Map<string, (message: PluginMessage) => void> = new Map();
  private messageQueue: PluginMessage[] = [];

  constructor() {
    this.initializeConnections();
    this.setupMessageListener();
  }

  /**
   * Initialize plugin connections
   */
  private initializeConnections(): void {
    const pluginTypes: PluginType[] = ['figma', 'photoshop', 'illustrator'];
    
    pluginTypes.forEach(type => {
      this.connections.set(type, {
        type,
        status: 'disconnected',
      });
    });
  }

  /**
   * Setup message listener for plugin communication
   */
  private setupMessageListener(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('message', (event) => {
        // Validate message origin and structure
        if (this.isValidPluginMessage(event.data)) {
          this.handlePluginMessage(event.data);
        }
      });
    }
  }

  /**
   * Validate plugin message
   */
  private isValidPluginMessage(data: unknown): data is PluginMessage {
    return (
      data !== null &&
      typeof data === 'object' &&
      'type' in data &&
      'pluginType' in data &&
      'timestamp' in data &&
      'messageId' in data
    );
  }

  /**
   * Handle incoming plugin message
   */
  private handlePluginMessage(message: PluginMessage): void {
    const handler = this.messageHandlers.get(message.type);
    
    if (handler) {
      handler(message);
    }

    // Update connection status based on message
    if (message.type === 'connect') {
      this.updateConnectionStatus(message.pluginType, 'connected');
    } else if (message.type === 'disconnect') {
      this.updateConnectionStatus(message.pluginType, 'disconnected');
    } else if (message.type === 'error') {
      const payload = message.payload as { error?: string };
      this.updateConnectionStatus(message.pluginType, 'error', payload?.error);
    }
  }

  /**
   * Update plugin connection status
   */
  private updateConnectionStatus(
    type: PluginType,
    status: PluginStatus,
    error?: string
  ): void {
    const connection = this.connections.get(type);
    if (connection) {
      connection.status = status;
      connection.lastConnected = status === 'connected' ? Date.now() : connection.lastConnected;
      connection.error = error;
    }
  }

  /**
   * Send message to plugin
   */
  private sendMessage(message: PluginMessage): void {
    if (typeof window !== 'undefined' && window.parent) {
      window.parent.postMessage(message, '*');
    } else {
      // Queue message if window is not available
      this.messageQueue.push(message);
    }
  }

  /**
   * Generate unique message ID
   */
  private generateMessageId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Register message handler
   */
  public onMessage(type: PluginMessageType, handler: (message: PluginMessage) => void): void {
    this.messageHandlers.set(type, handler);
  }

  /**
   * Get connection status for a plugin
   */
  public getConnectionStatus(type: PluginType): PluginConnection | undefined {
    return this.connections.get(type);
  }

  /**
   * Get all connection statuses
   */
  public getAllConnections(): PluginConnection[] {
    return Array.from(this.connections.values());
  }

  /**
   * Connect to a plugin
   */
  public async connect(type: PluginType): Promise<void> {
    this.updateConnectionStatus(type, 'connecting');

    const message: PluginMessage = {
      type: 'connect',
      pluginType: type,
      timestamp: Date.now(),
      messageId: this.generateMessageId(),
    };

    this.sendMessage(message);

    // Simulate connection timeout
    setTimeout(() => {
      const connection = this.connections.get(type);
      if (connection && connection.status === 'connecting') {
        this.updateConnectionStatus(
          type,
          'error',
          'Connection timeout - plugin not responding'
        );
      }
    }, 5000);
  }

  /**
   * Disconnect from a plugin
   */
  public disconnect(type: PluginType): void {
    const message: PluginMessage = {
      type: 'disconnect',
      pluginType: type,
      timestamp: Date.now(),
      messageId: this.generateMessageId(),
    };

    this.sendMessage(message);
    this.updateConnectionStatus(type, 'disconnected');
  }

  /**
   * Push palette to plugin
   */
  public async pushPalette(type: PluginType, palette: PaletteRecord): Promise<void> {
    const connection = this.connections.get(type);
    
    if (!connection || connection.status !== 'connected') {
      throw new Error(`Plugin ${type} is not connected`);
    }

    const message: PluginMessage = {
      type: 'push_palette',
      pluginType: type,
      payload: {
        palette: this.serializePalette(palette),
      },
      timestamp: Date.now(),
      messageId: this.generateMessageId(),
    };

    this.sendMessage(message);
  }

  /**
   * Pull palette from plugin
   */
  public async pullPalette(type: PluginType): Promise<PaletteRecord> {
    const connection = this.connections.get(type);
    
    if (!connection || connection.status !== 'connected') {
      throw new Error(`Plugin ${type} is not connected`);
    }

    return new Promise((resolve, reject) => {
      const messageId = this.generateMessageId();
      
      // Setup one-time handler for response
      const handler = (message: PluginMessage) => {
        if (message.messageId === messageId) {
          this.messageHandlers.delete('pull_palette');
          
          const payload = message.payload as { palette?: Record<string, unknown> };
          if (payload?.palette) {
            resolve(this.deserializePalette(payload.palette));
          } else {
            reject(new Error('No palette data received'));
          }
        }
      };

      this.onMessage('pull_palette', handler);

      const message: PluginMessage = {
        type: 'pull_palette',
        pluginType: type,
        timestamp: Date.now(),
        messageId,
      };

      this.sendMessage(message);

      // Timeout after 10 seconds
      setTimeout(() => {
        this.messageHandlers.delete('pull_palette');
        reject(new Error('Pull palette timeout'));
      }, 10000);
    });
  }

  /**
   * Push design tokens to plugin
   */
  public async pushTokens(type: PluginType, palette: PaletteRecord): Promise<void> {
    const connection = this.connections.get(type);
    
    if (!connection || connection.status !== 'connected') {
      throw new Error(`Plugin ${type} is not connected`);
    }

    const tokens = this.paletteToTokens(palette);

    const message: PluginMessage = {
      type: 'push_tokens',
      pluginType: type,
      payload: {
        tokens,
      },
      timestamp: Date.now(),
      messageId: this.generateMessageId(),
    };

    this.sendMessage(message);
  }

  /**
   * Convert palette to design tokens
   */
  private paletteToTokens(palette: PaletteRecord): DesignToken[] {
    return palette.colors.map((color, index) => ({
      name: `${palette.name.toLowerCase().replace(/\s+/g, '-')}-${index + 1}`,
      value: color,
      type: 'color' as const,
      description: `Color ${index + 1} from ${palette.name} palette`,
    }));
  }

  /**
   * Serialize palette for transmission
   */
  private serializePalette(palette: PaletteRecord): Record<string, unknown> {
    return {
      id: palette.id,
      name: palette.name,
      colors: palette.colors,
      tags: palette.tags || [],
      created: palette.created,
      modified: palette.modified,
      version: palette.version,
      author: palette.author,
    };
  }

  /**
   * Deserialize palette from plugin
   */
  private deserializePalette(data: Record<string, unknown>): PaletteRecord {
    return {
      id: data.id as string,
      name: (data.name as string) || 'Imported Palette',
      colors: (data.colors as string[]) || [],
      tags: (data.tags as string[]) || [],
      created: (data.created as number) || Date.now(),
      modified: (data.modified as number) || Date.now(),
      version: (data.version as number) || 1,
      author: (data.author as string) || 'Plugin Import',
    };
  }

  /**
   * Request status from plugin
   */
  public async requestStatus(type: PluginType): Promise<void> {
    const message: PluginMessage = {
      type: 'status_request',
      pluginType: type,
      timestamp: Date.now(),
      messageId: this.generateMessageId(),
    };

    this.sendMessage(message);
  }
}

// Export singleton instance
export const pluginService = new PluginService();
